import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { CheckCircle2, XCircle, BadgeCheck, PenSquare } from "lucide-react";

const statusStyle = {
  pending: "bg-[#E8DAB2]/70 text-amber-900",
  confirmed: "bg-[#D9E4E1] text-[#2f5448]",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-[#2C557E]/10 text-[#2C557E]",
};

const COLUMNS = ["Sl. No.", "DOA", "Patient Name", "Age", "DRPR ID", "Documents", "Contact Info", "Diagnosis", "Status", "Actions"];

export const AppointmentsTable = ({ appointments, onUpdateStatus, onMarkPaid, onEditDiagnosis }) => (
  <div className="bg-white rounded-2xl border border-[#E5EAE8] overflow-x-auto">
    <table className="w-full text-sm" data-testid="admin-appointments-table">
      <thead>
        <tr className="border-b-2 border-[#5E8B7E]/40 text-left">
          {COLUMNS.map((h) => (
            <th key={h} className="px-3 py-3 text-xs font-semibold text-[#2C557E] uppercase tracking-wide whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {appointments.map((a, idx) => (
          <tr key={a.id} data-testid={`admin-appt-${a.id}`} className="border-b border-[#EDF1EF] last:border-0 align-top hover:bg-[#F4F7F6]/60">
            <td className="px-3 py-3 text-slate-500">{idx + 1}</td>
            <td className="px-3 py-3 whitespace-nowrap">
              <div className="font-medium text-slate-800">{a.date}</div>
              <div className="text-xs text-slate-500">{a.slot} · {a.type === "online" ? "Online" : a.location || "Clinic"}</div>
            </td>
            <td className="px-3 py-3">
              <div className="font-medium text-slate-800">{a.patient_name}</div>
              <div className="text-xs text-slate-500">{a.service_name}</div>
              {a.notes && <div className="text-xs text-slate-400 italic max-w-[180px] truncate" title={a.notes}>"{a.notes}"</div>}
            </td>
            <td className="px-3 py-3 text-slate-700">{a.patient_age ?? "—"}</td>
            <td className="px-3 py-3">
              <span className="font-mono text-xs text-[#2C557E]">{a.patient_unique_id || "—"}</span>
              {a.patient_abha_id && <div className="font-mono text-[10px] text-slate-400">{a.patient_abha_id}</div>}
            </td>
            <td className="px-3 py-3" data-testid={`admin-appt-docs-${a.id}`}>
              {a.has_documents
                ? <Badge className="bg-[#D9E4E1] text-[#2f5448] hover:bg-[#D9E4E1] rounded-full">Yes</Badge>
                : <Badge variant="outline" className="rounded-full text-slate-400">No</Badge>}
            </td>
            <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">
              <div>{a.patient_phone}</div>
              <div className="text-slate-400">{a.patient_email}</div>
            </td>
            <td className="px-3 py-3 max-w-[200px]">
              <button
                data-testid={`admin-diagnosis-btn-${a.id}`}
                onClick={() => onEditDiagnosis(a)}
                className="text-left w-full group"
                title="Click to add/edit diagnosis"
              >
                {a.diagnosis ? (
                  <span className="text-xs text-slate-700 line-clamp-2">{a.diagnosis}</span>
                ) : (
                  <span className="text-xs text-slate-400 italic">Add diagnosis</span>
                )}
                <PenSquare className="w-3 h-3 inline-block ml-1 text-slate-300 group-hover:text-[#2C557E]" />
              </button>
            </td>
            <td className="px-3 py-3">
              <Badge className={`${statusStyle[a.status]} rounded-full capitalize hover:${statusStyle[a.status]}`}>{a.status}</Badge>
              {a.payment_status === "paid" && <div className="text-[10px] text-green-700 mt-1">Paid ₹{a.fee}</div>}
              {a.payment_status === "pay_at_clinic" && <div className="text-[10px] text-amber-700 mt-1">Fee at clinic</div>}
            </td>
            <td className="px-3 py-3">
              <div className="flex flex-col gap-1.5">
                {a.status === "pending" && (
                  <>
                    <Button data-testid={`admin-confirm-btn-${a.id}`} size="sm" onClick={() => onUpdateStatus(a.id, "confirmed")} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] h-7 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
                    </Button>
                    <Button data-testid={`admin-cancel-btn-${a.id}`} size="sm" variant="outline" onClick={() => onUpdateStatus(a.id, "cancelled")} className="rounded-full text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs">
                      <XCircle className="w-3 h-3 mr-1" /> Decline
                    </Button>
                  </>
                )}
                {a.status === "confirmed" && (
                  <>
                    {a.type === "online" && a.payment_status !== "paid" && (
                      <Button data-testid={`admin-mark-paid-btn-${a.id}`} size="sm" onClick={() => onMarkPaid(a.id)} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] h-7 text-xs">
                        Mark Paid ₹{a.fee}
                      </Button>
                    )}
                    <Button data-testid={`admin-complete-btn-${a.id}`} size="sm" variant="outline" onClick={() => onUpdateStatus(a.id, "completed")} className="rounded-full h-7 text-xs">
                      <BadgeCheck className="w-3 h-3 mr-1" /> Completed
                    </Button>
                  </>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
